import { computed } from 'vue'
import type { ECompany } from '@/shared/enum/company.enum'
import { ECampaign } from '@/shared/enum/campaign.enum'
import { EChannel } from '@/shared/enum/channel.enum'
import { EProfileType } from '@/shared/enum/profile-type.enum'
import { useTokenStore } from '@/stores/token'
import { safeParse } from '@/shared/utils/json.util'

export const useToken = () => {
  const tokenStore = useTokenStore()

  const tokenValue = computed((): string => {
    return tokenStore.token || ''
  })

  const msisdn = computed((): string => {
    return tokenStore.decodedToken?.msisdn || ''
  })

  const profileType = computed((): EProfileType | null => {
    return tokenStore.decodedToken?.profileType || null
  })

  const company = computed((): ECompany | null => {
    return tokenStore.decodedToken?.companyId || null
  })

  const campaign = computed((): ECampaign => {
    return (tokenStore.decodedToken?.cc || ECampaign.NULL).toUpperCase() as ECampaign
  })

  const flowType = computed(() => {
    return tokenStore.decodedToken?.flowType || ''
  })

  const is3G = computed((): boolean => {
    return !tokenStore.decodedToken?.wifi
  })

  const isValidToken = computed((): boolean => {
    if (!tokenStore.decodedToken) return false
    return tokenStore.decodedToken.exp * 1000 > new Date().getTime()
  })

  const isProfiledToken = computed((): boolean => {
    if (!tokenStore.decodedToken) return false
    return Boolean(tokenStore.decodedToken.profileType)
  })

  const isModemPrepaidProfile = computed((): boolean => {
    return Number(tokenStore.decodedToken?.profileType) === EProfileType.MODEM_PREPAID
  })

  const paymentsId = computed<Record<string, number>>(() =>
    safeParse<Record<string, number>>(tokenStore.decodedToken?.payments, {})
  )

  const paymentsUser = computed<number[]>(() => Object.keys(paymentsId.value).map(Number))

  const app = computed<string>(() => {
    return tokenStore.decodedToken?.app || ''
  })

  const isMCW = computed<boolean>(() => {
    const raw = tokenStore.decodedToken?.app
    const appValue = safeParse<string>(raw, raw || '')
    return appValue === EChannel.MCWEB
  })

  const isMCAPP = computed((): boolean => {
    return tokenStore.decodedToken?.app === EChannel.MCAPP
  })

  return {
    app,
    campaign,
    company,
    flowType,
    msisdn,
    paymentsId,
    paymentsUser,
    profileType,
    tokenValue,
    is3G,
    isProfiledToken,
    isModemPrepaidProfile,
    isValidToken,
    isMCW,
    isMCAPP
  }
}
